/**
 * PreS6ResultsPanel
 *
 * Displays the system recommendation (Pre-S6 Results) as a read-only reference
 * panel during and after the S6 group ranking session.
 *
 * Purpose: make the model's recommendation visible to the group so they can
 * deliberate against a concrete baseline, not from scratch.
 */

import { useState } from 'react';
import type { PreS6RankingSnapshot, GroupOptionResult } from '../../types/prioritization/groupSession';

interface PreS6ResultsPanelProps {
  snapshot: PreS6RankingSnapshot;
  /** Post-S6 ranked options, used to compute deltas when results are available. */
  postS6Results?: GroupOptionResult[];
  defaultExpanded?: boolean;
}

export function PreS6ResultsPanel({
  snapshot,
  postS6Results,
  defaultExpanded = false,
}: PreS6ResultsPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const rankDeltas = buildRankDeltas(snapshot, postS6Results);

  return (
    <div
      style={{
        border: '1px solid var(--rule-bright)',
        borderRadius: '10px',
        marginBottom: '24px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%',
          background: 'var(--bg-raised)',
          border: 'none',
          borderBottom: expanded ? '1px solid var(--rule)' : 'none',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: '10px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--text-ter)',
              background: 'var(--rule)',
              padding: '3px 8px',
              borderRadius: '4px',
              flexShrink: 0,
            }}
          >
            System Recommendation
          </span>
          <span
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--text-sec)',
            }}
          >
            Pre-S6 Results · {snapshot.rankedItems.length} items
          </span>
        </div>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '12px',
            color: 'var(--text-ter)',
            flexShrink: 0,
          }}
        >
          {expanded ? '▲ hide' : '▼ show'}
        </span>
      </button>

      {/* Body */}
      {expanded && (
        <div style={{ padding: '16px 18px' }}>
          <p
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--text-ter)',
              lineHeight: 1.6,
              margin: '0 0 16px',
            }}
          >
            This ranking was produced by the prioritization model from S1–S5 inputs. It is the
            baseline your group is deliberating against. It will not be changed by this session.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {snapshot.rankedItems.map((item) => {
              const delta = rankDeltas[item.itemId];
              return (
                <div
                  key={item.itemId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: 'var(--surface)',
                    border: '1px solid var(--rule)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                  }}
                >
                  {/* Rank badge */}
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'var(--rule-bright)',
                      color: 'var(--text-sec)',
                      fontFamily: 'var(--mono)',
                      fontWeight: 700,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {item.rank}
                  </div>

                  {/* Title */}
                  <span
                    style={{
                      fontFamily: 'var(--sans)',
                      fontSize: '14px',
                      color: 'var(--text-pri)',
                      flex: 1,
                    }}
                  >
                    {item.title}
                  </span>

                  {/* Score */}
                  <span
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: '11px',
                      color: 'var(--text-ter)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.score.toFixed(2)}
                  </span>

                  {/* Delta pill (shown only when post-S6 results are available) */}
                  {delta !== undefined && delta !== 0 && (
                    <DeltaPill delta={delta} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Snapshot metadata */}
          <div
            style={{
              marginTop: '14px',
              paddingTop: '12px',
              borderTop: '1px solid var(--rule)',
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap',
            }}
          >
            <SnapshotMeta
              label="Captured"
              value={new Date(snapshot.capturedAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            />
            {snapshot.toolName && (
              <SnapshotMeta label="Tool" value={snapshot.toolName} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Delta pill ───────────────────────────────────────────────────────────────

function DeltaPill({ delta }: { delta: number }) {
  const elevated = delta > 0;
  const label = elevated ? `+${delta} vs model` : `${delta} vs model`;
  const color = elevated ? 'var(--green)' : 'var(--coral)';
  const bg = elevated ? 'var(--green-dim)' : 'var(--coral-dim)';

  return (
    <span
      style={{
        fontFamily: 'var(--mono)',
        fontSize: '10px',
        color,
        background: bg,
        borderRadius: '4px',
        padding: '2px 7px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
      title={elevated ? 'Elevated by group vs system recommendation' : 'Lowered by group vs system recommendation'}
    >
      {label}
    </span>
  );
}

// ─── Snapshot metadata stat ───────────────────────────────────────────────────

function SnapshotMeta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '10px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-ter)',
          marginBottom: '2px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '12px',
          color: 'var(--text-sec)',
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Rank delta computation ───────────────────────────────────────────────────

function buildRankDeltas(
  snapshot: PreS6RankingSnapshot,
  postS6Results?: GroupOptionResult[],
): Record<string, number> {
  if (!postS6Results || postS6Results.length === 0) return {};

  const deltas: Record<string, number> = {};
  for (const item of snapshot.rankedItems) {
    const post = postS6Results.find((r) => r.itemId === item.itemId);
    if (post) {
      // Positive = group elevated the item (lower rank number = higher priority)
      deltas[item.itemId] = item.rank - post.rank;
    }
  }
  return deltas;
}
